<template>
  <div
    class="collection-card"
    :data-product-id="product.id"
    @click="clickCollectionCard"
  >
    <div
      ref="collectionImg"
      class="collection-card__media-wrapper collection-card__media slider-images-navigation-button"
    >
      <template v-if="showSwiper && isNavigationClicked && SwiperComponent && SwiperSlideComponent && !isHideImages">
        <client-only>
          <nuxt-error-boundary @error="() => {}">
            <component
              :is="SwiperComponent"
              :modules="swiperModules"
              :loop="true"
              :navigation="true"
              :pagination="true"
              :virtual="imageIds.length > 3"
              :space-between="5"
              @swiper="onSliderInit"
              @slide-change="onSlideChange"
            >
              <component
                :is="SwiperSlideComponent"
                v-for="(imageId, index) in imageIds"
                :key="`product-image-${product.id}-${imageId}`"
              >
                <nuxt-link
                  :to="product.url"
                  @click.capture="(event: Event) => clickLinkProduct(event)"
                >
                  <nuxt-img
                    :src="String(imageId)"
                    width="565px"
                    height="397px"
                    fit="default"
                    loading="lazy"
                    :alt="product.name"
                    class="fit-contain"
                    :modifiers="{'border': 'default'}"
                  />
                  <nuxt-img
                    class="__placeholder swiper-lazy-preloader"
                    :src="String(imageId)"
                    width="565"
                    height="397"
                    fit="default"
                    quality="1"
                    loading="lazy"
                    :alt="product.name"
                    :modifiers="{'border': 'default'}"
                  />
                </nuxt-link>
              </component>
            </component>
          </nuxt-error-boundary>
        </client-only>
      </template>
      <template v-else-if="!isHideImages">
        <nuxt-link
          :to="product.url"
          @click.capture="(event: Event) => clickLinkProduct(event)"
        >
          <nuxt-img
            :src="String(imageIds?.[0])"
            width="565px"
            height="397px"
            fit="default"
            loading="lazy"
            :alt="product.name"
            class="fit-contain __single"
            :modifiers="{'border': 'default'}"
            @load="isMainImageLoaded = true"
          />
          <nuxt-img
            v-show="!isMainImageLoaded"
            class="__placeholder"
            :src="String(imageIds?.[0])"
            width="565"
            height="397"
            fit="default"
            quality="1"
            loading="lazy"
            :alt="product.name"
            :modifiers="{'border': 'default'}"
          />
        </nuxt-link>
        <div
          v-if="imageIds.length > 1"
          class="collection-card__nav-trigger"
        >
          <button
            class="swiper-button-prev"
            @click.stop="() => initSwiper(-1)"
          ></button>
          <button
            class="swiper-button-next"
            @click.stop="() => initSwiper(1)"
          ></button>
        </div>
        <div
          v-if="imageIds.length > 1 && !isNavigationClicked"
          class="swiper-pagination swiper-pagination-bullets swiper-pagination-horizontal"
        >
          <span
            v-for="(imageId, index) in imageIds"
            :key="`pagination-${imageId}`"
            class="swiper-pagination-bullet"
            :class="{ 'swiper-pagination-bullet-active': index === 0 }"
          ></span>
        </div>
      </template>

      <template v-if="Boolean(productsDots.length > 0 && activeSlide === 0)">
        <div class="collection-card__dots">
          <DotProduct
            v-for="(info, index) in productsDots"
            :key="`DotProduct-${index}`"
            :info="info"
          />
        </div>
      </template>
    </div>

    <div class="collection-card__body">
      <div class="collection-card__main">
        <div class="collection-card__name-parent">
          <div class="tooltip">
            <nuxt-link
              :to="product.url"
              class="collection-card__name tooltip__item"
              @click.capture="(event: Event) => clickLinkProduct(event)"
            >
              <span v-html="product.name" />
            </nuxt-link>
            <div class="tooltip__hover">
              <span v-html="product.name" />
            </div>
          </div>
        </div>
        <div class="collection-card__composition-price">
          <template
            v-for="(priceIcon, index) in priceCompositionIcons"
            :key="`collection-card__composition-priceIcon-${priceIcon}`"
          >
            <div
              v-if="index > 0"
              class="--sign"
            >
              <img
                loading="lazy"
                src="~/assets/img/svg/common/plus.svg"
                alt="плюс"
              />
            </div>
            <div class="--element">
              <nuxt-img
                :src="String(priceIcon)"
                width="16"
                height="16"
                fit="fill"
                loading="lazy"
                decoding="auto"
                :alt="`${product.name}-price-icon`"
              />
            </div>
          </template>
          <template v-if="priceCompositionIcons.length > 0">
            <div class="--sign">
              <img
                loading="lazy"
                src="~/assets/img/svg/common/equals.svg"
                alt="плюс"
              />
            </div>
          </template>
          <div class="--price">
            {{ $numberFormat(product.price) }} ₽
          </div>
        </div>
      </div>
      <div class="collection-card__delimiter" />
      <div class="collection-card__additionally">
        <div class="collection-card__count-info">
          <div v-if="countChildren">{{ countChildren }}</div>
          <template v-if="countColors">
            <TooltipColors
              :colors="colorsList"
              :product-url="product.url"
            >
              <div>{{ countColors }}</div>
            </TooltipColors>
          </template>
        </div>

        <template v-if="tagsCard.length > 0">
          <div class="collection-card__tags">
            <template
              v-for="(tagCard, index) in tagsCard"
              :key="`collection-card__${index}`"
            >
              <TagItem
                :tag="tagCard"
              />
            </template>
          </div>
        </template>
      </div>
    </div>
    <div class="collection-card__footer">
      <nuxt-link
        :to="`${product.url}#products`"
        class="btn btn-primary"
        @click.capture="(event: Event) => clickLinkProduct(event)"
      >Товары коллекции</nuxt-link>
    </div>

    <div class="collection-card__wish">
      <client-only>
        <WishBtn :product-id="Number(product.id)" />
      </client-only>
    </div>

    <template v-if="Boolean(discountLabel.label)">
      <div
        class="collection-card__promo"
        :class="{'tooltip': discountLabel.tooltip}"
      >
        <div v-html="discountLabel.label" class="tooltip__item"/>
        <div
          v-if="discountLabel.tooltip"
          v-html="discountLabel.tooltip"
          class="tooltip__hover"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { useNuxtApp } from 'nuxt/app';
import { computed, ref, nextTick } from 'vue';
import type { IProduct } from '#sitis/internal/controllers/products/models/Product';
import getDiscountLabel from '~/utils/getDiscountLabel';
import DotProduct from '~/components/catalog/collection-card/DotProduct.vue';
import type { IWishlistItem } from '#sitis/internal/controllers/wish/models/WishlistItem';
import { scrollToElement } from '~/utils/scrollToElement';
import useEcommerce from '~/composables/useEcommerceYandex';
import { sortValuesBigToSmall } from '~/utils/getCharacteristic';

const TagItem = defineAsyncComponent(() => import('./collection-card/TagItem.vue'));
const WishBtn = defineAsyncComponent(() => import('~/components/widget/WishBtn.vue'));
const TooltipColors = defineAsyncComponent(() => import('./product-card/TooltipColors.vue'));

const { addEcommerce } = useEcommerce();
const props = defineProps<{
	isCatalog?: boolean | undefined;
  pageProduct?: number | undefined;
  product: IProduct | IWishlistItem;
  isHideImages?: boolean | undefined;
  isDisabledSwiper?: boolean | undefined;
}>();
let classId = "";
const {
  $ym,
  $caseWords
} = useNuxtApp();
const { $numberFormat } = useNuxtApp();
const { $viewport } = useNuxtApp();
const $route = useRoute();
const $router = useRouter();

const activeSlide = ref(0);
const collectionImg = ref<HTMLDivElement | null>(null);
const showSwiper = ref<boolean>(false);
const isNavigationClicked = ref<boolean>(false);
const isMainImageLoaded = ref<boolean>(false);

// Touch события для свайпа на мобильных
const touchStartX = ref<number>(0);
const touchStartY = ref<number>(0);
const touchEndX = ref<number>(0);
const touchEndY = ref<number>(0);
const isSwiping = ref<boolean>(false);

// Модули и компоненты Swiper загружаются динамически при клике
const swiperModules = ref<any[]>([]);
const SwiperComponent = ref<any>(null);
const SwiperSlideComponent = ref<any>(null);

const imageIds = computed(() => {
  const listIds = props?.product?.imageIds || [];
  return [...listIds].splice(0, 4);
});

const tagsCard = computed(() => {
  const product: any = props?.product;
  const promotions = product?.promotions || [];
  if (promotions.length <= 0) {
    return [];
  }

  return promotions
    .filter((t: any) => Boolean(t?.dynamicFields?.is_additional && t?.iconFileId))
    .sort((a: any, b: any) => {
      const sortA = a?.dynamicFields?.sort || 0;
      const sortB = b?.dynamicFields?.sort || 0;

      if (sortA < sortB) {
        return 1;
      }
      if (sortA > sortB) {
        return -1;
      }
      return 0;
    })
    .map((t: any) => {
      return {
        icon: t?.icon,
        description: t?.description || ''
      };
    });
});
const countColors = computed(() => {
  const collection_color: any = (props?.product?.shortCharacteristics || []).find((t: any) => t.slug === 'collection_color');
  const count = (collection_color?.values || []).length;
  if (count <= 0) {
    return '';
  }
  const word = $caseWords(count, ['цвет', 'цвета', 'цветов']);
  return [count, word].join(' ');
});
const colorsList = computed(() => {
  const collection_color: any = (props?.product?.shortCharacteristics || []).find((t: any) => t.slug === 'collection_color');
  if (!collection_color) {
    return [];
  }

  const brandInternal: any = (props?.product?.shortCharacteristics || []).find((t: any) => t.slug === 'brand-internal')?.values?.[0]?.value;
  const sortValues = sortValuesBigToSmall(collection_color);
  const itemsColors = [...(sortValues?.values || [])]
    .map((_value: any) => {
      let _strValue: string = String(_value?.value || '');
      if (!!brandInternal) {
        _strValue = _strValue.replace(`${brandInternal}: `, '');
      }
      return {
        ..._value,
        value: _strValue
      }
    });

  return itemsColors
});

const countChildren = computed(() => {
  const count = props?.product?.productSetChildrenCount || 0;
  if (count <= 0) {
    return '';
  }
  const word = $caseWords(count, ['предмет', 'предмета', 'предметов']);
  return [count, word].join(' ');
});

const discountLabel = computed(() => getDiscountLabel(props.product));
const productsDots = computed(() => {
  return (props?.product?.productSetImageDots || [])
    .filter((t: any) => Boolean(t.status))
});
const priceCompositionIcons = computed(() => {
  const iconsProductPrice: any = (props.product?.shortCharacteristics || []).find((t: any) => t.slug === 'icons-product-price');
  const value = iconsProductPrice?.values?.[0]?.value || '';
  if (value[0] !== '[' || value[value.length - 1] !== ']') {
    return [];
  }
  return JSON.parse(value);
});

const sliderSwiper: any = ref(null);
const onSliderInit = (item: any) => {
  sliderSwiper.value = item;

  // Если был отложенный клик на навигацию, выполняем переключение слайда
  if (pendingDirection.value !== null) {
    nextTick(() => {
      if (pendingDirection.value! > 0) {
        sliderSwiper.value?.slideNext();
      } else {
        sliderSwiper.value?.slidePrev();
      }
      pendingDirection.value = null;
    });
  }
}

const onSlideChange = (event: any) => {
  activeSlide.value = event?.realIndex || 0;
};

const pendingDirection = ref<number | null>(null);

const initSwiper = async (direction: number) => {
  if (!isNavigationClicked.value) {
    // Загружаем компоненты и модули Swiper только при первом клике
    const [swiperVue, swiperModulesImport] = await Promise.all([
      import('swiper/vue'),
      import('swiper/modules')
    ]);

    SwiperComponent.value = swiperVue.Swiper;
    SwiperSlideComponent.value = swiperVue.SwiperSlide;
    swiperModules.value = [swiperModulesImport.Navigation, swiperModulesImport.Pagination, swiperModulesImport.Virtual];

    pendingDirection.value = direction;
    isNavigationClicked.value = true;
  } else if (sliderSwiper.value) {
    // Если свайпер уже инициализирован, сразу переключаем слайд
    if (direction > 0) {
      sliderSwiper.value.slideNext();
    } else {
      sliderSwiper.value.slidePrev();
    }
  }
};

// Обработчики touch событий для свайпа
const handleTouchStart = (e: TouchEvent) => {
  if (isNavigationClicked.value || imageIds.value.length <= 1) {
    return;
  }

  touchStartX.value = e.touches[0].clientX;
  touchStartY.value = e.touches[0].clientY;
  isSwiping.value = false;
};

const handleTouchMove = (e: TouchEvent) => {
  if (isNavigationClicked.value || imageIds.value.length <= 1) {
    return;
  }

  touchEndX.value = e.touches[0].clientX;
  touchEndY.value = e.touches[0].clientY;

  const deltaX = touchEndX.value - touchStartX.value;
  const deltaY = touchEndY.value - touchStartY.value;

  // Если горизонтальное движение больше вертикального, считаем это свайпом
  if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
    isSwiping.value = true;
    e.preventDefault();
  }
};

const handleTouchEnd = () => {
  if (isNavigationClicked.value || imageIds.value.length <= 1) {
    return;
  }

  const deltaX = touchEndX.value - touchStartX.value;
  const deltaY = touchEndY.value - touchStartY.value;

  // Минимальное расстояние для определения свайпа (50px)
  const minSwipeDistance = 50;

  // Проверяем, что горизонтальное движение больше вертикального (это именно свайп влево/вправо)
  if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
    // Свайп влево = next (1), свайп вправо = prev (-1)
    const direction = deltaX < 0 ? 1 : -1;
    initSwiper(direction);
  }

  // Сброс значений с небольшой задержкой, чтобы успеть отменить клик
  setTimeout(() => {
    touchStartX.value = 0;
    touchStartY.value = 0;
    touchEndX.value = 0;
    touchEndY.value = 0;
    isSwiping.value = false;
  }, 100);
};

const savePageProduct: Ref<number | null | undefined> = ref(null);
const clickLinkProduct = (event?: Event) => {
  // Предотвращаем переход по ссылке если был свайп
  if (isSwiping.value && event) {
    event.preventDefault();
    return;
  }

  if (typeof $ym === 'function') {
    $ym('reachGoal', 'click_card_collection_button_link');
  }

  if (!props.isCatalog) {
    return;
  }

  const { pathname } = window.location;

  const searchObj: any = $route.query || {};
  searchObj.page = savePageProduct.value;
  let searchStr: any = [];
  Object.keys(searchObj).map((searchKey: string) => {
    searchStr.push(`${searchKey}=${searchObj[searchKey]}`);
  });
  searchStr = searchStr.join('&');

  const newUrl = [pathname, searchStr].filter((t: string) => !!t).join('?');
  $router.options.history.push(newUrl);

  let coockiesData: {} | string = {
    productId: props.product.id,
    fullPath: window.location.pathname
  };
  coockiesData = JSON.stringify(coockiesData);

  const coockyCatalog: any = useCookie('cookie-scroll-catalog');
  coockyCatalog.value = coockiesData;
};
const scrollToProductCatalog = () => {
  if (!props.isCatalog) {
    return;
  }

  const cookieScrollCatalog: any = useCookie('cookie-scroll-catalog');
  if (!cookieScrollCatalog.value) {
    return;
  }

  if (String(cookieScrollCatalog.value?.productId) !== String(props.product?.id)) {
    return;
  }

  const fullPath = window.location.pathname;
  if (fullPath !== cookieScrollCatalog.value?.fullPath) {
    return;
  }

  setTimeout(() => {
    scrollToElement(`[data-product-id="${props.product.id}"]`);
    cookieScrollCatalog.value = null;
  }, 501);
};

const clickCollectionCard = () => {
  addEcommerce('click', props.product as IProduct);
}

watch(() => props.isDisabledSwiper, () => {
  if (!sliderSwiper?.value) {
    return
  }
  if (props.isDisabledSwiper) {
    sliderSwiper?.value.disable();
  } else {
    sliderSwiper?.value.enable();
  }
});

onMounted(() => {
  showSwiper.value = true;
  // classId = String('10000000'.replace(/[018]/g, (c) => (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)));
  if (props.isCatalog) {
    savePageProduct.value = props.pageProduct;
    scrollToProductCatalog();
  }

  // Добавляем touch обработчики для свайпа
  if (collectionImg.value) {
    collectionImg.value.addEventListener('touchstart', handleTouchStart, { passive: true });
    collectionImg.value.addEventListener('touchmove', handleTouchMove, { passive: false });
    collectionImg.value.addEventListener('touchend', handleTouchEnd, { passive: true });
  }
});

onBeforeUnmount(() => {
  // Удаляем touch обработчики
  if (collectionImg.value) {
    collectionImg.value.removeEventListener('touchstart', handleTouchStart);
    collectionImg.value.removeEventListener('touchmove', handleTouchMove);
    collectionImg.value.removeEventListener('touchend', handleTouchEnd);
  }
});
</script>

<style lang="scss">
.collection-card {
	display: flex;
	flex-direction: column;
	border-radius: 20px;
	position: relative;
	z-index: 1;
	background-color: #F4F3F1;

	&:hover {
		z-index: 5;
		border-radius: 20px 20px 0 0;

		.collection-card__footer {
			display: block;
		}
	}

	& .swiper-pagination {
		position: absolute;
	}
}

.collection-card:not(:hover) {
	.collection-card__media-wrapper {
		.collection-dot-product,
		.swiper-button-next,
		.swiper-button-prev,
		.swiper-pagination {
			opacity: 0;
		}
	}
}

.collection-card__wish {
	position: absolute;
	top: 16px;
	right: 16px;
	z-index: 11;
}

.collection-card__media {
  border-radius: 20px 20px 5px 5px;
  position: relative;
  overflow: hidden;
  z-index: 0;
  background: #F4F3F1;

  .swiper {
    display: flex;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .swiper-slide {
    width: 100% !important;
    background-color: #F4F3F1;
  }

  .swiper-zoom-container {
    background-color: #F4F3F1;
  }

  .swiper-slide img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    mix-blend-mode: multiply;
    z-index: 5;
  }

  .swiper-pagination-bullet {
    background-color: white;
    opacity: 0.5;
    width: 5px;
    height: 5px;
  }

  .swiper-pagination-bullet-active {
    opacity: 1;
  }

  .__single {
    position: absolute;
    top: 0; left: 0;
    width: 100%;
    height: 100%;
    border: none;
    top: 0; left: 0;
    border-radius: 0;
    margin: 0!important;
    z-index: 1!important;
    animation: initial!important;
  }
  .__placeholder {
    position: absolute;
    top: 0; left: 0;
    width: 100%;
    height: 100%;
    border: none;
    top: 0; left: 0;
    border-radius: 0;
    margin: 0!important;
    z-index: -1!important;
    animation: initial!important;
  }

  &::after {
    content: "";
    float: left;
    padding-top: 70.153%;
  }
}

.collection-card__body {
	padding: 24px;
	box-sizing: border-box;
}

.collection-card__main {
	display: flex;
	align-items: center;
	justify-content: space-between;

	.tooltip {
		display: grid;
		flex: 1;
	}

	.tooltip__hover {
		z-index: 5;
		background-color: white;
		right: initial;
		left: 20px;
    max-width: 220px;
    width: max-content;
		transform: translate(0, -15px);
		border-radius: 15px;
    border-bottom-left-radius: 0;

		&:after {
			right: initial;
			left: 0;
			top: 100%;
      transform: translateY(-10px);
			border-right: 10px solid transparent;
			border-left: 15px solid white;
		}
	}
}

.collection-card__delimiter {
	margin: 12px 0;
	width: 100%;
	height: 1px;
	background-color: #B8B8B8;
	opacity: 0.1;
}

.collection-card__footer {
	display: none;
	padding: 24px;
	padding-top: 0;
	box-sizing: border-box;
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	background-color: #F4F3F1;
	border-radius: 0 0 20px 20px;

	.btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 60px;
		font-weight: 600;
	}
}

.collection-card__name-parent {
  display: flex;
  justify-content: flex-start;
  margin-right: 16px;
}

.collection-card__name {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	color: #000;
	font-size: 20px;
	font-weight: 600;
	line-height: 120%;
}

.collection-card__composition-price {
	display: flex;
	align-items: center;

	.--sign {
		margin: 0 4px;
	}

	.--element {
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 7px;
		background: white;
		width: 28px;
		height: 28px;
		padding: 6px;
		box-sizing: border-box;

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
		}
	}

	.--price {
		color: #000;
		font-size: 16px;
		font-weight: 600;
		line-height: 100%;
		white-space: nowrap;
	}
}

.collection-card__additionally {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.collection-card__count-info {
	display: flex;
	margin-top: -8px;
	margin-left: -8px;

	& > * {
		border-radius: 7px;
		background: #FFF;
		padding: 1px 16px;
		box-sizing: border-box;
		margin-top: 8px;
		margin-left: 8px;
		color: #000;
		font-size: 12px;
		font-weight: 500;
	}
}

.collection-card__promo {
	position: absolute;
	top: 16px;
	left: 16px;
	z-index: 10;
	border-radius: 10px;
	background: #FFF;
	box-sizing: border-box;
	color: #252525;
	font-size: 12px;
	font-weight: 600;

  .tooltip__item {
    padding: 7px 12px;
  }
  .tooltip__hover {
    background: white;
    bottom: initial;
    top: 100%;
    transform: translateY(10px);
    right: initial;
    left: 0;
    border-radius: 15px;
    border-top-left-radius: 0;
    max-width: 320px;
    width: max-content;

    &:after {
      top: -10px;
      bottom: initial;
      right: initial;
      left: 0;
      border-left: 15px solid white;
      border-right-color: transparent;
    }
  }
}

.collection-card__tags {
	display: flex;
	margin-top: -8px;
	margin-left: -8px;

	& > * {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 28px;
		height: 28px;
		padding: 4px;
		box-sizing: border-box;
		margin-top: 8px;
		margin-left: 8px;
		border-radius: 7px;
		background: #FFF;

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
		}
	}
}

.collection-card__nav-trigger {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  pointer-events: none;

  .swiper-button-prev,
  .swiper-button-next {
    pointer-events: auto;
    cursor: pointer;
  }
}

@media (max-width: 1023px) {
	.collection-card__main {
		flex-direction: column;
		align-items: flex-start;
	}
  .collection-card__name-parent {
    margin-right: 0;
    margin-bottom: 16px;
  }
	.collection-card__name {
		overflow: hidden;
		position: relative;
		width: 100%;
	}
	.collection-card__body {
		padding-bottom: 16px;
	}
	.collection-card__delimiter {
		display: none;
	}
	.collection-card__additionally {
		margin-top: 16px;
	}
	.collection-card__count-info > * {
		padding: 8px 12px;
		box-sizing: border-box;
		line-height: 100%;
	}
	.collection-card__footer {
		display: block;
		position: relative;
		top: 0;

		.btn {
			height: 48px;
			font-size: 12px;
		}
	}
	.collection-card__composition-price .--sign {
		margin: 0 6px;
	}

	.collection-card:not(:hover) {
		.collection-card__media {
			.swiper-button-next,
			.swiper-button-prev {
				display: flex;
				opacity: 1;
			}

			.swiper-pagination {
				display: block;
				opacity: 1;
			}
		}
	}

  .collection-card .collection-card__media .collection-dot-product {
		display: none !important;
	}

  .collection-card__dots {
    display: none;
  }
}

@media screen and (max-width: 860px) {
  .collection-card__promo {
    .tooltip__hover {
      max-width: 220px;
      width: max-content;
    }
  }
}

@media screen and (max-width: 640px) {
  .collection-card__promo {
    .tooltip__hover {
      max-width: 320px;
      width: max-content;
    }
  }
}

</style>
